import {
	Component,
	inject,
	OnDestroy,
	OnInit,
	ChangeDetectionStrategy,
} from '@angular/core';
import { Location, NgFor, NgIf, AsyncPipe } from '@angular/common';
import {
	FormControl,
	FormGroup,
	Validators,
	ReactiveFormsModule,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatBottomSheet } from '@angular/material/bottom-sheet';

import { ActivatedRoute, Router } from '@angular/router';
import { Loading } from '../../../applications/loading';
import { ReplaySubject, Subject, takeUntil } from 'rxjs';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { RoleService } from '@lmc-app/core/services/role.service';
import { PositionService } from '@lmc-app/core/services/position.service';
import { CompanyService } from '@lmc-app/core/services/company.service';
import { DepartmentService } from '@lmc-app/core/services/department.service';
import { EmployeeService } from '@lmc-app/core/services/employee.service';
import { RapidUserAccountService } from '@lmc-app/core/services/rapid-user-account.service';
import { MatSelectSearchUtil } from '@lmc-app/utils/mat-select-search.util';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { MatButton } from '@angular/material/button';
import { ScrollToTopButton } from '../../../shared/components/scroll-to-top/scroll-to-top.button';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSelectSearchComponent } from 'ngx-mat-select-search';
import { MatSlideToggle } from '@angular/material/slide-toggle';

@Component({
	selector: 'app-update-account',
	templateUrl: './update-account.component.html',
	styleUrls: ['./update-account.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		MatButton,
		ScrollToTopButton,
		ReactiveFormsModule,
		MatFormField,
		MatLabel,
		MatInput,
		MatSelect,
		MatOption,
		MatSelectSearchComponent,
		NgFor,
		NgIf,
		MatSlideToggle,
		AsyncPipe,
	],
})
export class UpdateAccountComponent implements OnInit, OnDestroy {
	private readonly passwordRe =
		/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[`~!@#$%\^&*\(\)\-_\+\=\[\]\{\}\\\|;:\'",\.\<\>?\/]).*?/;

	private employeeId: any; // For Edit ngOnInit() to get the ID
	protected rapidUserToken: number;

	protected updateAccountForm = new FormGroup({
		email: new FormControl(null, [
			Validators.required,
			Validators.minLength(3),
			Validators.maxLength(45),
			Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]{2,}$'),
		]),
		password: new FormControl(null, [
			Validators.minLength(12),
			Validators.maxLength(45),
			Validators.pattern(this.passwordRe),
		]),
		role_id: new FormControl(null, Validators.required),
		full_name: new FormControl(null, Validators.required),
		position: new FormControl(null),
		zoho_owner_id: new FormControl(null, [Validators.required]),
		extension: new FormControl(null, [
			Validators.min(100),
			Validators.max(99999999),
			Validators.pattern(/^[0-9]+$/),
		]),
		company_id: new FormControl(null),
		department_id: new FormControl(null),
		employee_status: new FormControl(null),
		create_rapid: new FormControl(null),
		rapid_user_token: new FormControl({ disabled: true }),
	});
	private users: IdName[] = [];
	private roles: IdName[] = [];
	private positions: IdName[] = [];
	private companies: IdName[] = [];
	private departments: IdName[] = [];
	protected usersFilter: FormControl<string | null> = new FormControl<string>(
		''
	);
	protected filteredUsers: ReplaySubject<IdName[]> = new ReplaySubject<
		IdName[]
	>(1);
	protected rolesFilter: FormControl<string | null> = new FormControl<string>(
		''
	);
	protected filteredRoles: ReplaySubject<IdName[]> = new ReplaySubject<
		IdName[]
	>(1);
	protected positionsFilter: FormControl<string | null> =
		new FormControl<string>('');
	protected filteredPositions: ReplaySubject<IdName[]> = new ReplaySubject<
		IdName[]
	>(1);
	protected companiesFilter: FormControl<string | null> =
		new FormControl<string>('');
	protected filteredCompanies: ReplaySubject<IdName[]> = new ReplaySubject<
		IdName[]
	>(1);
	protected departmentsFilter: FormControl<string | null> =
		new FormControl<string>('');
	protected filteredDepartments: ReplaySubject<IdName[]> = new ReplaySubject<
		IdName[]
	>(1);
	protected _onDestroy = new Subject<void>();
	private readonly _snackBar = inject(MatSnackBar);
	private readonly _bottomSheet = inject(MatBottomSheet);

	constructor(
		private _location: Location,
		private readonly apiService: LegacyApiService,
		private readonly roleService: RoleService,
		private readonly positionService: PositionService,
		private readonly companyService: CompanyService,
		private readonly departmentService: DepartmentService,
		private readonly employeeService: EmployeeService,
		private readonly rapidUserAccountService: RapidUserAccountService,
		private readonly router: Router,
		private readonly activatedRoute: ActivatedRoute
	) {}

	ngOnInit() {
		this.employeeId = this.activatedRoute.snapshot?.paramMap.get('employee_id'); // This will retrieve the ID that is linked on the routerlink from the html

		this.initialData();

		this.usersFilter.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((searchValue) => {
				MatSelectSearchUtil.filterAllStringFields(
					this.users,
					searchValue,
					this.filteredUsers
				);
			});

		this.rolesFilter.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((searchValue) => {
				MatSelectSearchUtil.filterAllStringFields(
					this.roles,
					searchValue,
					this.filteredRoles
				);
			});

		this.rolesFilter.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((searchValue) => {
				MatSelectSearchUtil.filterAllStringFields(
					this.roles,
					searchValue,
					this.filteredRoles
				);
			});

		this.positionsFilter.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((searchValue) => {
				MatSelectSearchUtil.filterAllStringFields(
					this.positions,
					searchValue,
					this.filteredPositions
				);
			});

		this.companiesFilter.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((searchValue) => {
				MatSelectSearchUtil.filterAllStringFields(
					this.companies,
					searchValue,
					this.filteredCompanies
				);
			});

		this.departmentsFilter.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((searchValue) => {
				MatSelectSearchUtil.filterAllStringFields(
					this.departments,
					searchValue,
					this.filteredDepartments
				);
			});
	}

	initialData(): Promise<any> {
		return new Promise((resolve, reject) => {
			Promise.all([
				this.getUsers(),
				this.getRoles(),
				this.getPositions(),
				this.getCompanies(),
				this.getEmployee(this.employeeId),
				this.getRapidUserAccount(this.employeeId),
			])
				.then(() => {
					resolve(true);
				})
				.catch(() => reject(false));
		});
	}

	backClicked() {
		this._location.back();
	}

	getUsers() {
		this.apiService.getZohoUsers().subscribe({
			next: (res) => {
				this.users = JSON.parse(res.message.body).users.map(
					({ id, full_name }) => ({
						id,
						name: full_name,
					})
				);
				this.filteredUsers.next(this.users.slice());
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

	getRoles() {
		this.roleService.getRoles().subscribe({
			next: (res) => {
				this.roles = res.map(({ role_id, description }) => ({
					id: role_id,
					name: description,
				}));
				this.filteredRoles.next(this.roles.slice());
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

	getPositions() {
		this.positionService.getPositions().subscribe({
			next: (res) => {
				this.positions = res.map(({ position }) => ({
					id: position,
					name: position,
				}));
				this.filteredPositions.next(this.positions.slice());
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

	getCompanies() {
		this.updateAccountForm.controls.company_id.disable();
		this.updateAccountForm.controls.department_id.disable();
		this.companyService.getCompanies().subscribe({
			next: (res) => {
				this.companies = res.map(({ company_id, company_name }) => ({
					id: company_id,
					name: company_name,
				}));
				this.updateAccountForm.controls.company_id.enable();
				this.filteredCompanies.next(this.companies.slice());
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

	onCompanyChanged(companyId: number) {
		this.updateAccountForm.controls.department_id.reset();
		this.getDepartments(companyId);
	}

	getDepartments(companyId: number) {
		this.updateAccountForm.controls.department_id.disable();
		if (companyId) {
			this.departmentService.getDepartments(companyId).subscribe({
				next: (res) => {
					this.departments = res.map(({ department_id, department_name }) => ({
						id: department_id,
						name: department_name,
					}));
					this.updateAccountForm.controls.department_id.enable();
					this.filteredDepartments.next(this.departments.slice());
				},
				error: (error) => {
					console.error(error);
				},
			});
		}
	}

	getEmployee(employee_id: number) {
		this.employeeService.getEmployee(employee_id).subscribe({
			next: (res) => {
				this.getDepartments(res.company_id);
				this.updateAccountForm.controls.email.setValue(res.email);
				this.updateAccountForm.controls.role_id.setValue(res.roleID);
				this.updateAccountForm.controls.full_name.setValue(res.full_name);
				this.updateAccountForm.controls.position.setValue(res.position);
				this.updateAccountForm.controls.zoho_owner_id.setValue(
					res.zoho_owner_id
				);
				this.updateAccountForm.controls.extension.setValue(res.extension);
				this.updateAccountForm.controls.company_id.setValue(res.company_id);
				this.updateAccountForm.controls.department_id.setValue(
					res.department_id
				);
				this.updateAccountForm.controls.employee_status.setValue(
					res.employee_status
				);
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

	getRapidUserAccount(employee_id: number) {
		this.rapidUserAccountService.getRapidUserAccount(employee_id).subscribe({
			next: (res) => {
				this.rapidUserToken = res.token;
				this.updateAccountForm.controls.rapid_user_token.setValue(res.token);
				this.updateAccountForm.controls.rapid_user_token.disable();
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

	updateAccount() {
		this._bottomSheet.open(Loading, {
			disableClose: true,
			data: { message: 'Updating Account' },
		});
		const employee = this.convertFormToJson();
		this.employeeService.updateEmployee(employee).subscribe({
			next: (res) => {
				this._snackBar.open(
					`Account for ${res.full_name} is updated.`,
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
				this._bottomSheet.dismiss();
				this.router.navigate([`/admin-settings/account`]);
			},
			error: (error) => {
				this._bottomSheet.dismiss();
				console.error(error);
				this._snackBar.open(
					(error.error &&
						error.error.message &&
						(error.error.message.name || error.error.message)) ||
						'Error updating account.',
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
			},
		});
		if (this.updateAccountForm.controls.create_rapid.value) {
			this.rapidUserAccountService.createRapidUserAccount(employee).subscribe({
				next: (res) => {
					this._snackBar.open(
						`Rapid User Account for ${res.full_name} is created.`,
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
					this._bottomSheet.dismiss();
				},
				error: (error) => {
					this._bottomSheet.dismiss();
					console.error(error);
					this._snackBar.open(
						(error.error &&
							error.error.message &&
							(error.error.message.name || error.error.message)) ||
							'Error updating account.',
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
				},
			});
		}
	}

	cloneAccount() {
		this.router.navigate([`/admin-settings/account/create`], {
			queryParams: {
				role_id: this.updateAccountForm.controls.role_id.value,
				position: this.updateAccountForm.controls.position.value,
				company_id: this.updateAccountForm.controls.company_id.value,
				department_id: this.updateAccountForm.controls.department_id.value,
			},
		});
	}

	convertFormToJson() {
		const newAccount = this.updateAccountForm.getRawValue();
		newAccount['employee_id'] = this.employeeId;
		newAccount['platform'] = 'lmc';
		return newAccount;
	}

	ngOnDestroy() {
		this._onDestroy.next();
		this._onDestroy.complete();
	}
}
